import { getPointsExperiences } from 'actions/pointsExperiences'
import {
  useGetAvailableRewardsQuery,
  useResolveCaseMutation,
} from 'api/recoveryAi'
import SharedGrantRewardModal, {
  GrantRewardPayload,
} from 'components/GrantRewardModal'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectCurrentMerchantUser } from 'selectors/merchantUser'
import { selectExperience } from 'selectors/pointsExperience'
import { useAbility } from 'utilities/ability'

const resolveT = buildTranslate('recovery_ai.case_detail.resolve')

type Props = {
  isOpen: boolean
  conversationId: string
  // The case's customer, for the modal heading ("Grant reward to …"). The case
  // detail owns the conversation record, so it derives the display name and
  // hands it down; null when the conversation carries no name at all.
  customerName: string | null
  onClose: () => void
}

// The case detail's wiring for the promoted, presentational
// components/GrantRewardModal: this wrapper owns the data (available rewards),
// the mutation (resolveCase) and the permission check; the shared component owns
// the form. Behaviour is unchanged from before the promotion.
const GrantRewardModal = ({
  isOpen,
  conversationId,
  customerName,
  onClose,
}: Props) => {
  const ability = useAbility()
  const dispatch = useDispatch()
  // Points can only be granted where a points experience exists — the same gate
  // the customer-profile grant modal uses. No experience → the modal renders
  // reward-only, with no radio group.
  const pointsExperience = useSelector(selectExperience)

  // The LOGGED-IN OPERATOR (not the case's customer): the modal names their
  // address as where the customer's replies will land, because the BE sets it as
  // the Reply-To on the reply email. Read here rather than threaded from
  // CaseDetail (which computes its own for ResolveWithReplyModal) because this
  // wrapper is the surface's data owner. Null until the merchant user is in the
  // store, which hides the footer.
  const currentMerchantUser = useSelector(selectCurrentMerchantUser)
  const operatorEmail = currentMerchantUser?.email ?? null

  // Nothing else on this page loads the points experience, so the gate above
  // would read a permanently-empty store and hide the points path from every
  // merchant that has one. Fetch it once if it isn't there — the same
  // unconditional guard scenes/Users/View/Counters uses (deliberately NOT gated
  // on a Points ability: the precedent isn't, and this only reads config).
  useEffect(() => {
    if (!pointsExperience) {
      dispatch(getPointsExperiences())
    }
  }, [dispatch, pointsExperience])
  // Skip while closed and reuse the cached list rather than refetching on every
  // reopen — the available-rewards catalog is edited on a different surface and
  // rarely changes mid-session. (No mutation invalidates RecoveryAvailableRewards,
  // so the only refresh is RTK's keepUnusedDataFor expiry once the modal closes.)
  // `isError` is aliased away from the resolveCase mutation's isError below —
  // this one is the CATALOG's load state (the field's own failure copy), that
  // one is the failed-grant banner. Passed through so a broken catalog can't
  // masquerade as a merchant with no rewards to grant here either.
  //
  // Deliberately still the RAI route, while the Reports surface reads the
  // narrower `order_ratings/available_rewards` (AI-1017). Same merchant, same
  // GrantRewardModal, same AvailableReward type — two different reward sets:
  // this one is every published template, that one is published +
  // granted_program_visible backed by an active granted program. Each matches
  // its own resolve path's acceptance, so neither offers a reward its resolve
  // would reject. Keeping the flag-gated route here is correct because this
  // surface only exists when `recovery-ai` is ON.
  const {
    data: rewards = [],
    isFetching,
    isError: isRewardsError,
  } = useGetAvailableRewardsQuery(undefined, { skip: !isOpen })
  const [resolveCase, { isLoading, isError, reset }] = useResolveCaseMutation()

  // Defense-in-depth (D6): CaseDetail only mounts this for managers, but
  // re-check so the write control can never render for a view-only operator.
  // Kept at this call site rather than inside the shared component — the Reports
  // rating detail gates on a different subject.
  if (ability.cannot('manage', 'MerchantSettings')) return null

  // The shared modal hands back the resolve body minus the id; the case's id is
  // this surface's to add. `.unwrap()` so a failure rejects and the modal stays
  // open on its error banner.
  const handleConfirm = (payload: GrantRewardPayload) =>
    resolveCase({ id: conversationId, ...payload }).unwrap()

  return (
    <SharedGrantRewardModal
      isOpen={isOpen}
      customerName={customerName}
      showPointsOption={!!pointsExperience}
      operatorEmail={operatorEmail}
      rewards={rewards}
      isFetchingRewards={isFetching}
      hasRewardsError={isRewardsError}
      onConfirm={handleConfirm}
      isSubmitting={isLoading}
      isError={isError}
      onResetError={reset}
      errorMessage={resolveT('error')}
      onClose={onClose}
    />
  )
}

export default GrantRewardModal
